/**
 * Job Level API Contract
 *
 * Mirrors: backend/src/routes/jobLevel.routes.ts
 * 1:1 parity with existing Express routes
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { CreateJobLevelInputSchema, UpdateJobLevelInputSchema, ReorderJobLevelsInputSchema, JobLevelResponseSchema, OrgChartDataSchema, } from "@hourly/api/jobLevel";
// ============================================
// Job Level Contracts
// ============================================
/**
 * POST /job-levels
 * Create a new job level
 * Requires: job-level:create permission on the company
 * Returns 201 Created on success
 */
export const createJobLevel = oc
    .route({
    method: "POST",
    path: "/job-levels",
    summary: "Create a new job level",
    tags: ["JobLevel"],
    successStatus: 201,
})
    .input(CreateJobLevelInputSchema)
    .output(JobLevelResponseSchema);
/**
 * GET /job-levels
 * Get all job levels for a company
 * Requires: job-level:view permission on the company
 */
export const listJobLevels = oc
    .route({
    method: "GET",
    path: "/job-levels",
    summary: "List job levels for a company",
    tags: ["JobLevel"],
})
    .input(z.object({}))
    .output(z.array(JobLevelResponseSchema));
/**
 * PATCH /job-levels/reorder
 * Reorder job levels (drag and drop)
 * Requires: job-level:reorder permission on the company
 */
export const reorderJobLevels = oc
    .route({
    method: "PATCH",
    path: "/job-levels/reorder",
    summary: "Reorder job levels",
    tags: ["JobLevel"],
})
    .input(ReorderJobLevelsInputSchema)
    .output(z.array(JobLevelResponseSchema));
/**
 * GET /job-levels/{id}
 * Get a job level by ID
 * Requires: job-level:view permission on the company
 */
export const getJobLevel = oc
    .route({
    method: "GET",
    path: "/job-levels/{id}",
    summary: "Get job level by ID",
    tags: ["JobLevel"],
})
    .input(z.object({ id: z.uuid() }))
    .output(JobLevelResponseSchema);
/**
 * PUT /job-levels/{id}
 * Update a job level
 * Requires: job-level:update permission on the company
 */
export const updateJobLevel = oc
    .route({
    method: "PUT",
    path: "/job-levels/{id}",
    summary: "Update a job level",
    tags: ["JobLevel"],
})
    .input(z.object({ id: z.uuid() }).merge(UpdateJobLevelInputSchema))
    .output(JobLevelResponseSchema);
/**
 * DELETE /job-levels/{id}
 * Delete a job level
 * Requires: job-level:delete permission on the company
 * Returns 204 No Content on success
 */
export const deleteJobLevel = oc
    .route({
    method: "DELETE",
    path: "/job-levels/{id}",
    summary: "Delete a job level",
    tags: ["JobLevel"],
    successStatus: 204,
})
    .input(z.object({ id: z.uuid() }))
    .output(z.void());
/**
 * GET /org-chart
 * Get organization chart data (levels × families × roles)
 * Requires: job-level:view permission on the company
 */
export const getOrgChart = oc
    .route({
    method: "GET",
    path: "/org-chart",
    summary: "Get organization chart data",
    tags: ["JobLevel"],
})
    .input(z.object({}))
    .output(OrgChartDataSchema);
// ============================================
// Contract Router
// ============================================
export const jobLevelContract = {
    create: createJobLevel,
    list: listJobLevels,
    get: getJobLevel,
    update: updateJobLevel,
    delete: deleteJobLevel,
    reorder: reorderJobLevels,
    getOrgChart: getOrgChart,
};
//# sourceMappingURL=jobLevel.js.map